import React, { useState } from 'react';
import {Navigate, useNavigate} from "react-router-dom";
import User from '../models/User'

import NavBar from "../components/NavBar";
import Footer from "../components/Footer";
import ErrorToast from "../components/ErrorToast";

import { signup } from "../services/user-service";
import { useAuth } from "../context/AuthContext";

const SignUpPage = () => {
    const [form, setForm] = useState({
        firstName: '',
        lastName: '',
        email: '',
        password: '',
        confirmPassword: '',
    });

    const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
    const [errorToast, setErrorToast] = useState('');
    const [loading, setLoading] = useState(false);
    const { isAuthenticated } = useAuth();

    const navigate = useNavigate();

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setForm(prev => ({ ...prev, [name]: value }));
    };

    const validate = (): boolean => {
        const errs: Record<string,string> = {};
        if (form.firstName.length > 50) {
            errs.firstName = 'First name must be 50 characters or fewer';
        }
        if (form.lastName.length > 50) {
            errs.lastName = 'Last name must be 50 characters or fewer';
        }
        if (form.email.length > 100) {
            errs.email = 'Email must be 100 characters or fewer';
        }

        if (form.password !== form.confirmPassword) {
            errs.confirmPassword = 'Passwords do not match';
        } else if (form.password.length < 8 || form.password.length > 15) {
            errs.password = 'Password must be between 8 and 15 characters long';
        } else if (!/[a-z]/.test(form.password)) {
            errs.password = 'Password must include at least one lowercase letter';
        } else if (!/[A-Z]/.test(form.password)) {
            errs.password = 'Password must include at least one uppercase letter';
        } else if (!/[0-9]/.test(form.password)) {
            errs.password = 'Password must include at least one number';
        } else if (!/[^a-zA-Z0-9]/.test(form.password)) {
            errs.password = 'Password must include at least one special character';
        }

        setFieldErrors(errs);
        return Object.keys(errs).length === 0;
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setFieldErrors({});
        setErrorToast('');

        if(!validate()) {
            setLoading(false);
            return;
        }

        const newUser: User = {
            firstName: form.firstName,
            lastName: form.lastName,
            email: form.email,
            password: form.password
        };
        try {
            const response = await signup(newUser);
            if (response?.status === 201) {
                navigate('/confirm', {
                    state: {
                        email: form.email,
                        message: 'Sign-up successful! Please check your email for a confirmation code.'
                    }
                });
            } else if (response?.status === 409) {
                setFieldErrors({ email: 'Account already exists for this email' });
            } else {
                setErrorToast('Unable to create account at this time. Please try again later.');
            }
        } catch {
            setErrorToast('Unable to create account at this time. Please try again later.');
        } finally {
            setLoading(false);
        }
    };

    return (
        isAuthenticated ? <Navigate to="/dashboard" /> :
            <div className="flex flex-col min-h-screen bg-gray-50" >
            <NavBar />
            <div className="flex-grow mt-16">
                <div className="max-w-md mx-auto mt-10 p-6 bg-white rounded shadow ">
                    <form
                        onSubmit={handleSubmit}
                        className="w-full max-w-md space-y-6 rounded-xl"
                    >
                        <h2 className="text-2xl font-bold text-crumbblue mb-6 text-center">
                        Create Your CrumbCRM Account
                        </h2>

                        <label className="block mb-4" htmlFor="firstName">
                            <span className="text-sm text-gray-700">First Name</span>
                            <input
                                data-testid="first-name"
                                type="text"
                                name="firstName"
                                value={form.firstName}
                                onChange={handleChange}
                                className="mt-1 w-full px-4 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-crumbblue"
                                required
                            />
                            {fieldErrors.firstName && (
                                <div data-testid="first-name-error" className="text-red-600 text-sm mt-1">
                                    {fieldErrors.firstName}
                                </div>
                            )}
                        </label>

                        <label className="block mb-4" htmlFor="lastName">
                            <span className="text-sm text-gray-700">Last Name</span>
                            <input
                                data-testid="last-name"
                                type="text"
                                name="lastName"
                                value={form.lastName}
                                onChange={handleChange}
                                className="mt-1 w-full px-4 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-crumbblue"
                                required
                            />
                            {fieldErrors.lastName && (
                                <div data-testid="last-name-error" className="text-red-600 text-sm mt-1">
                                    {fieldErrors.lastName}
                                </div>
                            )}
                        </label>

                        <label className="block mb-4" htmlFor="email">
                            <span className="text-sm text-gray-700">Email</span>
                            <input
                                data-testid="email"
                                type="email"
                                name="email"
                                value={form.email}
                                onChange={handleChange}
                                className="mt-1 w-full px-4 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-crumbblue"
                                required
                            />
                            {fieldErrors.email && (
                                <div data-testid="email-error" className="text-red-600 text-sm mt-1">
                                    {fieldErrors.email}
                                </div>
                            )}
                        </label>

                        <label className="block mb-4" htmlFor="password">
                            <span className="text-sm text-gray-700">Password</span>
                            <input
                                data-testid="password"
                                type="password"
                                name="password"
                                value={form.password}
                                onChange={handleChange}
                                className="mt-1 w-full px-4 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-crumbblue"
                                required
                            />
                            {fieldErrors.password && (
                                <div data-testid="password-error" className="text-red-600 text-sm mt-1">
                                    {fieldErrors.password}
                                </div>
                            )}
                        </label>

                        <label className="block mb-6" htmlFor="confirmPassword">
                            <span className="text-sm text-gray-700">Confirm Password</span>
                            <input
                                data-testid="confirm-password"
                                type="password"
                                name="confirmPassword"
                                value={form.confirmPassword}
                                onChange={handleChange}
                                className="mt-1 w-full px-4 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-crumbblue"
                                required
                            />
                            {fieldErrors.confirmPassword && (
                                <div
                                    data-testid="confirm-password-error"
                                    className="text-red-600 text-sm mt-1"
                                >
                                    {fieldErrors.confirmPassword}
                                </div>
                            )}
                        </label>

                        <button
                            data-testid="submit-button"
                            type="submit"
                            disabled={loading}
                            className="w-full bg-crumborange text-white font-semibold py-2 px-4 rounded-md hover:bg-orange-600 transition"
                        >
                            {loading ? 'Signing up...' : 'Sign up'}
                        </button>
                    </form>
                </div>
                <ErrorToast data-testid="error-toast" message={errorToast} clearError={() => setErrorToast('')} />
            </div>
            <Footer />
        </div>
    );
};

export default SignUpPage;